import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { ModePanelNavItem } from '@/components/layout/sidebar/ModePanelNavItem'
import { wsNavSectionStyle } from '@/lib/ws-nav'
import { testIds } from '@/lib/test-ids'
import type { Thread } from './types'
import { unreadCountForFolder, countThreadsForFolder } from './inbox-folder-filters'
import {
  INBOX_FOLDERS,
  inboxFolderLabel,
  type InboxFolderId,
} from './inbox-folder-types'
import { useInboxFolderSelection } from './use-inbox-folder-selection'
import { useWorkspace } from '@/providers/workspace-provider'

export type InboxFoldersModePanelProps = {
  threads: Thread[]
  dense?: boolean
}

/**
 * Mode-panel folder nouns only (no thread list). Selection syncs to main index
 * via inbox-folder-change.
 */
export function InboxFoldersModePanel({ threads, dense = false }: InboxFoldersModePanelProps) {
  const { activeWorkspaceIds } = useWorkspace()
  const { folderId, selectFolder } = useInboxFolderSelection()

  const visibleFolders = useMemo(() => {
    return INBOX_FOLDERS.filter((f) => {
      if (!f.hideIfEmpty) return true
      return countThreadsForFolder(threads, f.id, activeWorkspaceIds) > 0
    })
  }, [threads, activeWorkspaceIds])

  return (
    <nav
      className="ws-nav-section flex flex-col flex-1 min-h-0 overflow-y-auto py-1"
      style={wsNavSectionStyle()}
      aria-label="Inbox folders"
      data-testid={testIds.inbox.foldersModePanel}
    >
      {visibleFolders.map((folder) => {
        const Icon = folder.icon
        const active = folderId === folder.id
        const count = countThreadsForFolder(threads, folder.id, activeWorkspaceIds)
        const unread = unreadCountForFolder(threads, folder.id, activeWorkspaceIds)

        return (
          <ModePanelNavItem
            key={folder.id}
            active={active}
            dense={dense}
            onClick={() => selectFolder(folder.id)}
            title={inboxFolderLabel(folder.id)}
            data-testid={testIds.inbox.folderRow(folder.id)}
            icon={<Icon className={cn('shrink-0', dense ? 'h-3.5 w-3.5' : 'h-4 w-4')} />}
            label={folder.label}
            trailing={
              unread > 0 && folder.id !== 'archived' && folder.id !== 'snoozed' ? (
                <span
                  className={cn(
                    'shrink-0 tabular-nums rounded-full px-1.5 text-[10px] font-semibold leading-4 min-w-[18px] text-center',
                    active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                  )}
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              ) : count > 0 && (folder.id === 'archived' || folder.id === 'snoozed') ? (
                <span className="shrink-0 tabular-nums text-[10px] text-muted-foreground/70">
                  {count}
                </span>
              ) : null
            }
          />
        )
      })}
    </nav>
  )
}

export { type InboxFolderId }
